import {useCallback, useRef, useState} from "react";
import {DocumentNode} from "graphql";
import {useLazyQuery} from "@apollo/client";
import {useFlags} from "launchdarkly-react-client-sdk";
import {type GraphQLQuery} from "@aws-amplify/api-graphql";
import {features} from "../../features";
import {useAmplifyClients} from "../AmplifyClientProvider";
import {withRetry} from "./retry";
import {
  AmplifyClientKey,
  GraphqlResult,
  GraphqlVariables,
  LazyQueryTrigger,
  toError
} from "./types";

interface UseGraphqlLazyQueryOptions<
  TData,
  TVariables extends GraphqlVariables
> {
  /** Apollo DocumentNode — required during migration, removed after Apollo cleanup */
  document: DocumentNode;
  /** Amplify query string */
  amplifyQuery: string;
  /** Default variables (can be overridden at call time) */
  variables?: TVariables;
  /** Which Amplify client to use (defaults to "appsyncBff") */
  amplifyClient?: AmplifyClientKey;
  /** Apollo context (e.g., { endpoint: Endpoints.APPSYNC }) */
  context?: Record<string, unknown>;
  /** fetchPolicy */
  fetchPolicy?: "network-only" | "no-cache" | "cache-first";
}

/**
 * Framework-agnostic lazy query hook returning [triggerFn, { data, loading, error }].
 * Routes to Apollo or Amplify based on the migration feature flag.
 */
export function useGraphqlLazyQuery<
  TData = unknown,
  TVariables extends GraphqlVariables = GraphqlVariables
>(
  options: UseGraphqlLazyQueryOptions<TData, TVariables>
): [LazyQueryTrigger<TData, TVariables>, GraphqlResult<TData>] {
  const flags = useFlags();
  const useAmplify =
    flags[features.dev.amplifyClient.useAmplifyForAllOperations];

  // === Apollo path ===
  const [apolloTrigger, apolloResult] = useLazyQuery<TData, TVariables>(
    options.document,
    {
      context: options.context,
      fetchPolicy: options.fetchPolicy
    }
  );

  // === Amplify path ===
  const clients = useAmplifyClients();
  const client = clients?.[options.amplifyClient ?? "appsyncBff"] ?? null;
  const [amplifyData, setAmplifyData] = useState<TData | undefined>(undefined);
  const [amplifyLoading, setAmplifyLoading] = useState(false);
  const [amplifyError, setAmplifyError] = useState<Error | undefined>(
    undefined
  );
  const cancelledRef = useRef(false);
  // Tracks the latest trigger call so earlier in-flight requests don't overwrite state
  const callIdRef = useRef(0);

  const amplifyTrigger: LazyQueryTrigger<TData, TVariables> = useCallback(
    async (triggerOptions?) => {
      const vars = triggerOptions?.variables ?? options.variables;
      const thisCallId = ++callIdRef.current;
      cancelledRef.current = false;
      setAmplifyLoading(true);
      setAmplifyError(undefined);

      try {
        if (!client) {
          throw new Error("Amplify client not initialized");
        }
        const result = await withRetry(() =>
          client.graphql<GraphQLQuery<TData>>({
            query: options.amplifyQuery,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            variables: vars as any
          })
        );
        const data = result.data as TData;

        if (!cancelledRef.current && callIdRef.current === thisCallId) {
          setAmplifyData(data);
          setAmplifyLoading(false);
        }
        return {data};
      } catch (err) {
        const error = toError(err);
        if (!cancelledRef.current && callIdRef.current === thisCallId) {
          setAmplifyError(error);
          setAmplifyLoading(false);
        }
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, options.amplifyQuery]
  );

  // Wrap Apollo trigger to match our interface
  const apolloTriggerWrapper: LazyQueryTrigger<TData, TVariables> = useCallback(
    async (triggerOptions?) => {
      const vars = triggerOptions?.variables ?? options.variables;
      const context = triggerOptions?.context ?? options.context;

      const result = await apolloTrigger({
        variables: vars as TVariables,
        context
      });
      return result;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apolloTrigger, options.context]
  );

  if (useAmplify) {
    return [
      amplifyTrigger,
      {data: amplifyData, loading: amplifyLoading, error: amplifyError}
    ];
  }

  return [
    apolloTriggerWrapper,
    {
      data: apolloResult.data,
      loading: apolloResult.loading,
      error: apolloResult.error
    }
  ];
}
